// useAuth.js - Supabase Auth hook for session management
// HARDENED: Handles token refresh failures gracefully
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Custom hook for Supabase authentication state
 * Handles session checking, auth state changes, and sign out
 * 
 * HARDENED FEATURES:
 * - Detects token refresh failures and signs out gracefully
 * - Prevents error logging loops with refs
 * - Provides authError for display on LoginPage
 * - Periodic session health checks as backup
 * - Exposes derived values: authUserId, email for convenience
 */
export function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  // Refs to prevent error logging loops and track mount state
  const refreshErrorLogged = useRef(false);
  const isMounted = useRef(true);

  // Sign out function - memoized to use in effects
  const signOut = useCallback(async () => {
    try {
      console.log('[Auth] Signing out...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[Auth] Sign out error:', error);
        // Still clear local state even if signOut API fails
      }
      if (isMounted.current) {
        setSession(null);
        setUser(null);
        refreshErrorLogged.current = false;
      }
      console.log('[Auth] ✅ Signed out successfully');
    } catch (err) {
      console.error('[Auth] Sign out failed:', err);
      // Clear local state anyway
      if (isMounted.current) {
        setSession(null);
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    
    // Check initial session
    const checkSession = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[Auth] Error checking session:', error);
          // Check if it's a refresh-related error
          if (error.message?.toLowerCase().includes('refresh') ||
              error.message?.toLowerCase().includes('token') ||
              error.message?.toLowerCase().includes('expired')) {
            if (isMounted.current) {
              setAuthError('Session expired. Please sign in again.');
              setSession(null);
              setUser(null);
            }
            return;
          }
        }
        
        if (isMounted.current) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          if (currentSession) {
            console.log('[Auth] ✅ Authenticated as:', currentSession.user?.email);
          } else {
            console.log('[Auth] 🔒 No active session');
          }
        }
      } catch (err) {
        console.error('[Auth] Session check failed:', err);
        if (isMounted.current) {
          setSession(null);
          setUser(null);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    checkSession();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted.current) return;
        
        console.log('[Auth] 🔄 Auth state changed:', event);
        
        switch (event) {
          case 'SIGNED_IN':
            console.log('[Auth] ✅ User signed in:', newSession?.user?.email);
            setSession(newSession);
            setUser(newSession?.user ?? null);
            setAuthError(null);
            refreshErrorLogged.current = false;
            break;
            
          case 'SIGNED_OUT':
            console.log('[Auth] 👋 User signed out');
            setSession(null);
            setUser(null);
            refreshErrorLogged.current = false;
            break;
            
          case 'TOKEN_REFRESHED':
            console.log('[Auth] 🔄 Token refreshed successfully');
            setSession(newSession);
            setUser(newSession?.user ?? null);
            setAuthError(null);
            refreshErrorLogged.current = false;
            break;
            
          case 'USER_UPDATED':
            console.log('[Auth] 👤 User updated');
            setSession(newSession);
            setUser(newSession?.user ?? null);
            break;
            
          default:
            // Handle any other session updates
            if (newSession) {
              setSession(newSession);
              setUser(newSession?.user ?? null);
            }
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      isMounted.current = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Periodic session health check - catches refresh failures
  useEffect(() => {
    if (!session) return;

    const checkSessionHealth = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          // Log once to prevent loop
          if (!refreshErrorLogged.current) {
            refreshErrorLogged.current = true;
            console.error('[Auth] Session health check failed:', error.message);
            
            // Check if it's a refresh/token error
            const errorMsg = error.message?.toLowerCase() || '';
            if (errorMsg.includes('refresh') || 
                errorMsg.includes('token') ||
                errorMsg.includes('expired') ||
                errorMsg.includes('invalid') ||
                error.status === 401) {
              console.log('[Auth] Token refresh failed, signing out...');
              if (isMounted.current) {
                setAuthError('Session expired. Please sign in again.');
              }
              await signOut();
            }
          }
        } else if (!currentSession && session) {
          // Session was lost without explicit sign out
          if (!refreshErrorLogged.current) {
            refreshErrorLogged.current = true;
            console.log('[Auth] Session lost unexpectedly, clearing state...');
            if (isMounted.current) {
              setSession(null);
              setUser(null);
              setAuthError('Session ended. Please sign in again.');
            }
          }
        }
      } catch (err) {
        if (!refreshErrorLogged.current) {
          refreshErrorLogged.current = true;
          console.error('[Auth] Session health check error:', err);
        }
      }
    };

    // Check session health every 60 seconds
    const interval = setInterval(checkSessionHealth, 60000);
    
    return () => clearInterval(interval);
  }, [session, signOut]);

  return {
    // Core auth state
    session,
    user,
    loading,
    isAuthenticated: !!session,
    signOut,
    authError,
    
    // Derived convenience values (no new DB queries)
    // These are safe to use in UI without additional fetches
    authUserId: user?.id || null,
    email: user?.email || null,
  };
}

export default useAuth;
